import { iterableToArray, getEnvironmentVar } from './helpers';
import { containsDomChild, getStorageItem, removeStorageItem, setStorageItem, openNewTab, deepClone } from './starterHelpers';
import { AsperaError } from '../classes/AsperaError';
import { registerActivityCallback, upload, download, initHttpGateway, getFilesForUploadPromise, getFoldersForUploadPromise, removeTransfer, cancelTransfer, createDropzone, removeDropzone } from '@ibm-aspera/http-gateway-sdk-js';
import { removeTransfer as desktopRemoveTransfer, registerActivityCallback as desktopRegisterActivityCallback, startTransfer as desktopStartTransfer, stopTransfer as desktopStopTransfer, showSelectFileDialog as desktopShowSelectFileDialog, showDirectory as desktopShowDirectory, showSelectFolderDialog as desktopShowSelectFolderDialog, showPreferences as desktopShowPreferences, createDropzone as desktopCreateDropzone, removeDropzone as desktopRemoveDropzone, modifyTransfer as desktopModifyTransfer, getAllTransfers as desktopGetAllTransfers, resumeTransfer as desktopResumeTransfer, registerRemovedCallback as desktopRegisterRemovedCallback } from '@ibm-aspera/sdk';
import { Connect, ConnectInstaller } from '@ibm-aspera/connect-sdk-js';
import { Subject, from, of, throwError } from 'rxjs';
import { TransferPoller } from '../classes/TransferPoller';
import { SubManager } from '../classes/SubManager';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { storageKeys } from '../constants/constants';
import { forceUpdate } from '../services/ForceUpdateService';
import { promptForDecrypt } from './components';
import { notification } from '../services/NotificationService';
import { language } from '../services/LanguageService';
import { tracking } from '../services/TrackingService';
/**
 * Indicate if mobile SDK should be used (running in Aspera Mobile app)
 *
 * @returns - boolean indicating if Mobile SDK should be used
 */
export const runningWithAsperaMobile = () => {
    var _a;
    return !!(window.ReactNativeWebView && typeof ((_a = window.ReactNativeWebView) === null || _a === void 0 ? void 0 : _a.postMessage) === 'function');
};
/**
 * Send a message to React native.  This will safety check before sending. If object or array will convert to string before sending.
 *
 * @param message - data to send to Aspera Mobile
 */
export const sendMobileSdkMessage = (message) => {
    var _a;
    if (window.ReactNativeWebView && typeof ((_a = window.ReactNativeWebView) === null || _a === void 0 ? void 0 : _a.postMessage) === 'function') {
        let finalMessage = '';
        if (typeof message === 'object' || Array.isArray(message)) {
            finalMessage = JSON.stringify(message);
        }
        else if (typeof message === 'string') {
            finalMessage = message;
        }
        window.ReactNativeWebView.postMessage(finalMessage);
    }
    else {
        new AsperaError('sendMobileSdkMessage: tried to send message when SDK mobile was not running');
    }
};
/**
 * Get sorted (newer added items first) list of all transfers
 *
 * @returns an array of transfers to send to transferMonitor
 */
export const getProperListOfTransfers = () => {
    var _a;
    if ((_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.allTransfers) {
        return iterableToArray(window.asperaTransfer.allTransfers.values()).reverse();
    }
    return [];
};
/**
 * Get sorted (newer added items first) list of all transfers that are for running HTTP Gateway
 *
 * @returns an array of transfers to send to transferMonitor
 */
export const getRunningHttpGatewayTransfers = () => {
    return getProperListOfTransfers().filter(transfer => {
        const connectTransfer = transfer;
        return !!connectTransfer.httpGatewayTransfer && (connectTransfer.status === 'running' || connectTransfer.status === 'initiating' || connectTransfer.status === 'waiting');
    });
};
/** Trigger force update of transfer monitor (tell Transfer Monitor to update state) */
export const forceUpdateTransferMonitor = () => {
    var _a;
    (_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.transferMonitor.next({ transfers: getProperListOfTransfers() });
};
/**
 * Add transfers to allTransfers (or their updates) and push all transfers to transfer monitor as an update
 * If transfer is new or transfer was already completed last time set do not call transferStatusMonitor
 *
 * @param transfers - array of transfers to push to transfer monitor subscribers
 */
export const pushTransfers = (transfers) => {
    transfers.forEach(transferData => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u;
        const transfer = deepClone(transferData);
        const uiTransferId = ((_d = (_c = (_b = (_a = transfer === null || transfer === void 0 ? void 0 : transfer.transfer_spec) === null || _a === void 0 ? void 0 : _a.tags) === null || _b === void 0 ? void 0 : _b.aspera) === null || _c === void 0 ? void 0 : _c.aspera_ui) === null || _d === void 0 ? void 0 : _d.transfer_id) || ((_h = (_g = (_f = (_e = transfer === null || transfer === void 0 ? void 0 : transfer.start_spec) === null || _e === void 0 ? void 0 : _e.tags) === null || _f === void 0 ? void 0 : _f.aspera) === null || _g === void 0 ? void 0 : _g.aspera_ui) === null || _h === void 0 ? void 0 : _h.transfer_id);
        if (((_m = (_l = (_k = (_j = transfer === null || transfer === void 0 ? void 0 : transfer.transfer_spec) === null || _j === void 0 ? void 0 : _j.tags) === null || _k === void 0 ? void 0 : _k.aspera) === null || _l === void 0 ? void 0 : _l.aspera_ui) === null || _m === void 0 ? void 0 : _m.ignoreTransfer) || ((_r = (_q = (_p = (_o = transfer === null || transfer === void 0 ? void 0 : transfer.start_spec) === null || _o === void 0 ? void 0 : _o.tags) === null || _p === void 0 ? void 0 : _p.aspera) === null || _q === void 0 ? void 0 : _q.aspera_ui) === null || _r === void 0 ? void 0 : _r.ignoreTransfer)) {
            return;
        }
        ;
        let type = 'node';
        if (transfer.httpGatewayTransfer) {
            type = 'http';
        }
        else if (transfer.desktopSdkTransfer) {
            type = 'desktop';
        }
        else if (transfer.uuid) {
            type = 'connect';
        }
        const priorTransfer = (_s = window.asperaTransfer) === null || _s === void 0 ? void 0 : _s.allTransfers.get(transfer.uuid || transfer.id);
        if (transfer.id) {
            const nodeTransfer = transfer;
            const priorCheckTransfer = priorTransfer;
            if (nodeTransfer.status === 'running') {
                /** Handle UI controlled paused */
                const foundSession = (nodeTransfer.sessions || []).filter(item => item.status === 'running')[0];
                if (foundSession && foundSession.target_rate_kbps === 0) {
                    nodeTransfer.status = 'paused';
                }
            }
            /** Check other UI values (pausing, stopping, ) */
            if (priorCheckTransfer) {
                if (priorCheckTransfer.status === 'pausing' && nodeTransfer.status === 'running') {
                    nodeTransfer.status = 'pausing';
                }
                else if (priorCheckTransfer.status === 'canceling' && nodeTransfer.status === 'running') {
                    nodeTransfer.status = 'canceling';
                }
            }
        }
        if (uiTransferId && (priorTransfer && priorTransfer.status !== 'completed')) {
            (_t = window.asperaTransfer) === null || _t === void 0 ? void 0 : _t.transferStatusMonitor.next({ status: transfer.status, uiTransferId, transfer, type });
        }
        (_u = window.asperaTransfer) === null || _u === void 0 ? void 0 : _u.allTransfers.set(transfer.uuid || transfer.id, transfer);
    });
    forceUpdateTransferMonitor();
};
/**
 * Setup Connect transfer monitor. This is auto called by setupTransferMonitor.
 */
export const setupConnectOnlyTransferMonitor = () => {
    var _a, _b, _c;
    if (((_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.connectRunning) && ((_b = window.asperaTransfer) === null || _b === void 0 ? void 0 : _b.asperaWeb)) {
        (_c = window.asperaTransfer.asperaWeb) === null || _c === void 0 ? void 0 : _c.addEventListener(Connect.EVENT.TRANSFER, (_event, data) => {
            pushTransfers(data.transfers.filter(transfer => {
                var _a;
                if (transfer.status === 'removed') {
                    (_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.allTransfers.delete(transfer.uuid);
                    return false;
                }
                return true;
            }));
        });
    }
};
/**
 * Used internally to track desktop transfers from SDK and parse into Aspera Starter
 */
export const parseDesktopTransfers = (desktopTransfers) => {
    const transfers = desktopTransfers || [];
    transfers.forEach(item => {
        item.desktopSdkTransfer = true;
    });
    pushTransfers(transfers);
};
/**
 * Setup transfer monitor logic for Connect and HTTP Gateway
 */
export const setupTransferMonitor = (noConnect) => {
    var _a, _b;
    const subManager = new SubManager();
    if (!noConnect) {
        setupConnectOnlyTransferMonitor();
    }
    if (runningWithAsperaMobile() && ((_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.mobileTransferUpdater) && window.asperaTransfer.mobileTransferRemover && window.asperaTransfer.mobileGetInfo) {
        subManager.setSubscription(window.asperaTransfer.mobileTransferRemover.subscribe({
            next: id => {
                removeTransferItem(id);
            },
            error: error => {
                new AsperaError('setupTransferMonitor: Mobile transfer remover watcher error', error);
            },
        }));
        subManager.setSubscription(window.asperaTransfer.mobileTransferUpdater.subscribe({
            next: transfers => {
                pushTransfers(transfers.filter(transfer => {
                    var _a;
                    if (transfer.status === 'removed') {
                        (_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.allTransfers.delete(transfer.uuid);
                        return false;
                    }
                    return true;
                }));
            },
            error: error => {
                new AsperaError('setupTransferMonitor: Mobile transfer updater error', error);
            },
        }));
        subManager.setSubscription(window.asperaTransfer.mobileGetInfo.subscribe({
            next: () => {
                sendMobileSdkMessage({ purpose: 'INFO', data: {
                        libVersion: getEnvironmentVar('aspera_starter_version'),
                    } });
            },
            error: error => {
                new AsperaError('setupTransferMonitor: Mobile library version getter error', error);
            },
        }));
    }
    if ((_b = window.asperaTransfer) === null || _b === void 0 ? void 0 : _b.transferPoller) {
        subManager.setSubscription(window.asperaTransfer.transferPoller.transferUpdater.subscribe({
            next: transfers => {
                pushTransfers(transfers);
            },
            error: error => {
                new AsperaError('setupTransferMonitor: Transfer monitor error from poller', error);
            },
        }));
    }
    registerActivityCallback((data) => {
        pushTransfers(data.transfers);
    });
    desktopRegisterActivityCallback((data) => {
        parseDesktopTransfers((data === null || data === void 0 ? void 0 : data.transfers) || []);
    });
    desktopRegisterRemovedCallback(data => {
        // Do not pass type since this case it was removed by Desktop directly. Just remove from internal system.
        removeTransferItem(data.uuid);
    });
};
/**
 * Setup the drop watch for Connect and Gateway.  This is not setup until after a transfer system starts.
 * removeDropzone in HTTP Gateway to de-register if connect starts
 */
export const setupDropWatch = () => {
    var _a, _b, _c, _d;
    const dropCallback = (data) => {
        var _a, _b;
        (_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.fileDropMonitor.next({ event: data.event, files: ((_b = data.files) === null || _b === void 0 ? void 0 : _b.dataTransfer.files) || [] });
    };
    if ((_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.desktopSdkRunning) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        desktopCreateDropzone(dropCallback, 'body');
        removeDropzone('body');
    }
    else if (((_b = window.asperaTransfer) === null || _b === void 0 ? void 0 : _b.connectRunning) && window.asperaTransfer.asperaWeb) {
        (_c = window.asperaTransfer.asperaWeb) === null || _c === void 0 ? void 0 : _c.setDragDropTargets('body', { drop: true, allowPropagation: true }, dropCallback);
        removeDropzone('body');
        desktopRemoveDropzone('body');
    }
    else if ((_d = window.asperaTransfer) === null || _d === void 0 ? void 0 : _d.httpGatewayRunning) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        createDropzone(dropCallback, 'body');
        desktopRemoveDropzone('body');
    }
};
/**
 * Monitor drop events on the window for connect.  By default all (entire window) drop events are intercepted.
 *
 * @param querySelector - querySelector to use to limit files to only the needed dropzone
 *
 * @returns an observable that gets newly added files.  If empty array is returned ignore drop (it was not for you)
 */
export const monitorDrops = (querySelector) => {
    var _a;
    if ((_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.fileDropMonitor) {
        return window.asperaTransfer.fileDropMonitor.asObservable().pipe(map(data => {
            if (!querySelector || containsDomChild(querySelector, data.event.target)) {
                return data;
            }
            else {
                return { event: data.event, files: [] };
            }
        }));
    }
    else {
        return of({ event: {}, files: [] });
    }
};
/**
 * Subscribe to events related to Connect status changes
 * This is useful in some parts like showing `Download To`
 */
export const watchConnectStatusChange = () => {
    var _a, _b;
    if ((_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.connectStatusChange) {
        return ((_b = window.asperaTransfer.connectStatusChange) === null || _b === void 0 ? void 0 : _b.asObservable()) || of('FAILED');
    }
    else {
        return of('FAILED');
    }
};
/**
 * Subscribe to events related to transfer status changes
 * This is useful in some parts like refreshing files grid
 */
export const watchTransferStatusChange = () => {
    var _a, _b;
    if ((_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.transferStatusMonitor) {
        return ((_b = window.asperaTransfer.transferStatusMonitor) === null || _b === void 0 ? void 0 : _b.asObservable()) || of({ status: 'waiting', uiTransferId: 'UNKNOWN', transfer: undefined, type: 'connect' });
    }
    else {
        return of({ status: 'waiting', uiTransferId: 'UNKNOWN', transfer: undefined, type: 'connect' });
    }
};
/**
 * Monitor the frame from Connect for user changes to find out when connect is ready
 *
 * @param eventStatus - status from ConnectInstaller
 */
export const setConnectStatus = (eventStatus) => {
    var _a, _b, _c;
    if (Connect.STATUS.RUNNING === eventStatus && window.asperaTransfer) {
        window.asperaTransfer.connectRunning = true;
        window.asperaTransfer.connectStatus = Connect.STATUS.RUNNING;
        (_a = window.asperaTransfer.connectStatusChange) === null || _a === void 0 ? void 0 : _a.next(eventStatus);
        setupTransferMonitor();
        setupDropWatch();
        (_b = window.asperaTransfer.asperaWeb) === null || _b === void 0 ? void 0 : _b.version().then(data => {
            if (window.asperaTransfer) {
                window.asperaTransfer.connectAppVersion = data.version;
            }
        }).catch(() => { });
    }
    else {
        if (window.asperaTransfer) {
            (_c = window.asperaTransfer.connectStatusChange) === null || _c === void 0 ? void 0 : _c.next(eventStatus);
            window.asperaTransfer.connectStatus = eventStatus;
        }
    }
};
/**
 * Init the HTTP Gateway.
 */
const startHttpGateway = (serverUrl) => {
    let resolver;
    let needForce = false;
    let resolved = false;
    const promise = new Promise(resolve => {
        resolver = resolve;
    });
    initHttpGateway(serverUrl).then(response => {
        var _a;
        if (window.asperaTransfer) {
            window.asperaTransfer.httpGatewayRunning = true;
            window.asperaTransfer.httpVersion = response.version;
            (_a = window.asperaTransfer.connectStatusChange) === null || _a === void 0 ? void 0 : _a.next('HTTP_START');
        }
        resolved = true;
        setupDropWatch();
        resolver();
        if (needForce) {
            forceUpdate.triggerUpdate();
        }
    }).catch(error => {
        new AsperaError('startHttpGateway: Unable to start HTTP Gateway', { error, serverUrl });
        resolved = true;
        resolver();
    });
    setTimeout(() => {
        if (!resolved) {
            new AsperaError('startHttpGateway: Unable to start HTTP Gateway in time', { serverUrl });
            needForce = true;
            resolver();
        }
    }, 3000);
    return from(promise);
};
/**
 * Init the transfer core systems (this does not start Connect). Gateway will start if URL is available
 *
 * @remarks Transfer system is automatically init by AsperaAppWrapper. This should not be used directly unless you are not using AsperaAppWrapper.
 */
export const initTransferSystem = (httpGatewayUrl, ibmId, transferStorageCallback, transfersInitialStore, transferSystemData) => {
    if (window.asperaTransfer) {
        return of({});
    }
    window.asperaTransfer = {
        transferMonitor: new Subject(),
        allTransfers: new Map(),
        transferPoller: new TransferPoller(transferStorageCallback, transfersInitialStore),
        fileDropMonitor: new Subject(),
        transferStatusMonitor: new Subject(),
        connectStatusChange: new Subject(),
        httpGatewayUrl: getStorageItem(storageKeys.httpGatewayServerUrl) || httpGatewayUrl,
        transferSystemData: transferSystemData || {},
        mobileTransferUpdater: new Subject(),
        mobileTransferRemover: new Subject(),
        mobileFilePickerCallback: new Subject(),
        mobileGetInfo: new Subject(),
        desktopSdkRunning: false,
        preferringDestkop: false,
    };
    if (!runningWithAsperaMobile() && !getEnvironmentVar('desktop_mode')) {
        window.asperaTransfer.asperaWeb = new Connect({
            minVersion: getEnvironmentVar('connect_min_version'),
            dragDropEnabled: true,
            connectMethod: getEnvironmentVar('connect_method'),
        });
        window.asperaTransfer.asperaInstaller = new ConnectInstaller({
            sdkLocation: getEnvironmentVar('connect_url'),
            correlationId: ibmId,
        });
    }
    return window.asperaTransfer.httpGatewayUrl ? startHttpGateway(window.asperaTransfer.httpGatewayUrl) : of({});
};
/**
 * Monitor transfers for the current session
 *
 * @returns an observable that pushes transfers whenever they are updated or received.
 */
export const monitorTransfers = () => {
    var _a;
    if ((_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.transferMonitor) {
        return window.asperaTransfer.transferMonitor.asObservable();
    }
    else {
        return of({ transfers: [] });
    }
};
/**
 * Handle showing custom errors for transfer system calls that involve dialogs
 *
 * @param error - Error from transfer system calls that have dialogs
 *
 * @returns A throw error that will trigger the error for lower functions
 */
export const handleDialogErrors = (error) => {
    var _a;
    const testError = error;
    switch ((_a = testError === null || testError === void 0 ? void 0 : testError.debugData) === null || _a === void 0 ? void 0 : _a.code) {
        case -32005:
            notification.create('error', language.get('transferSystem.dialogError'), language.get('transferSystem.dialogErrorHelper'));
            break;
        case -32002:
            return of({ dataTransfer: { files: [] } });
    }
    return throwError(() => error);
};
/**
 * Show the file or folder picker
 *
 * @remarks Consumers should unsubscribe after next/error callback to avoid issues in Mobile mode.
 *
 * @param id - id for the form needing files or folders (used for HTTP Gateway)
 * @param folder - indicate if the folder picker should be shown
 *
 * @returns an observable that returns the file or folder
 */
export const openFileFolderPicker = (id, folder) => {
    var _a, _b, _c, _d, _e, _f, _g;
    if (runningWithAsperaMobile() && !((_b = (_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.transferSystemData) === null || _b === void 0 ? void 0 : _b.disableConnect) && ((_c = window.asperaTransfer) === null || _c === void 0 ? void 0 : _c.mobileFilePickerCallback)) {
        sendMobileSdkMessage({
            purpose: 'GET_FILES',
            data: { id, folder: !!folder },
        });
        return window.asperaTransfer.mobileFilePickerCallback.asObservable();
    }
    let finalObservable;
    if (typeof window.asperaOverrideShowSelectFileDialog === 'function' && typeof window.asperaOverrideShowSelectFolderDialog === 'function') {
        finalObservable = folder ? window.asperaOverrideShowSelectFolderDialog() : window.asperaOverrideShowSelectFileDialog();
    }
    else if ((_d = window.asperaTransfer) === null || _d === void 0 ? void 0 : _d.desktopSdkRunning) {
        finalObservable = (folder ? from(desktopShowSelectFolderDialog()) : from(desktopShowSelectFileDialog()));
    }
    else if (((_e = window.asperaTransfer) === null || _e === void 0 ? void 0 : _e.connectRunning) && ((_f = window.asperaTransfer) === null || _f === void 0 ? void 0 : _f.asperaWeb)) {
        finalObservable = from(folder ? window.asperaTransfer.asperaWeb.showSelectFolderDialogPromise() : window.asperaTransfer.asperaWeb.showSelectFileDialogPromise());
    }
    else if ((_g = window.asperaTransfer) === null || _g === void 0 ? void 0 : _g.httpGatewayRunning) {
        finalObservable = from(folder ? getFoldersForUploadPromise(id) : getFilesForUploadPromise(id));
    }
    else {
        finalObservable = throwError(() => new AsperaError('openFileFolderPicker: No transfer method is available for selecting files').getError());
    }
    return finalObservable.pipe(catchError(error => handleDialogErrors(error)));
};
/**
 * Show the Connect Preferences window
 */
export const openConnectPreferences = () => {
    var _a, _b, _c, _d, _e;
    const handleError = (error) => {
        new AsperaError('openConnectPreferences: unable to open client preferences', error);
        notification.create('error', language.get('transferSystem.unableConnectPreference'));
    };
    if ((_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.desktopSdkRunning) {
        (_b = desktopShowPreferences()) === null || _b === void 0 ? void 0 : _b.catch(handleError);
    }
    else if (((_c = window.asperaTransfer) === null || _c === void 0 ? void 0 : _c.connectRunning) && ((_d = window.asperaTransfer) === null || _d === void 0 ? void 0 : _d.asperaWeb)) {
        (_e = window.asperaTransfer.asperaWeb.showPreferences()) === null || _e === void 0 ? void 0 : _e.catch(handleError);
    }
    else {
        new AsperaError('openConnectPreferences: Client is not available');
    }
};
export const startConnectTransfer = (transferSpec, connectOptions, useDecrypt) => {
    const startTransferCallback = () => {
        var _a;
        if (typeof window.asperaOverrideTransferCall === 'function') {
            return window.asperaOverrideTransferCall(transferSpec, connectOptions);
        }
        if ((_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.asperaWeb) {
            return from(window.asperaTransfer.asperaWeb.startTransferPromise(transferSpec, connectOptions));
        }
        else {
            return throwError(() => new AsperaError('startConnectTransfer: AsperaWeb not found').getError());
        }
    };
    if (useDecrypt) {
        return promptForDecrypt().pipe(mergeMap(response => {
            if (response.passphrase) {
                transferSpec.content_protection = 'decrypt';
                transferSpec.content_protection_passphrase = response.passphrase;
            }
            return startTransferCallback();
        }));
    }
    else {
        return startTransferCallback();
    }
};
export const startDesktopTransfer = (transferSpec, connectOptions, useDecrypt) => {
    const startTransferCallback = () => {
        if (typeof window.asperaOverrideTransferCall === 'function') {
            return window.asperaOverrideTransferCall(transferSpec, connectOptions);
        }
        return from(desktopStartTransfer(transferSpec, connectOptions)).pipe(catchError(error => handleDialogErrors(error)));
    };
    if (useDecrypt) {
        return promptForDecrypt().pipe(mergeMap(response => {
            if (response.passphrase) {
                transferSpec.content_protection = 'decrypt';
                transferSpec.content_protection_passphrase = response.passphrase;
            }
            return startTransferCallback();
        }));
    }
    else {
        return startTransferCallback();
    }
};
const startTransferWithPrompt = (transferSpec, connectOptions, useDecrypt) => {
    var _a, _b, _c;
    if ((_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.desktopSdkRunning) {
        let observable = from(desktopShowSelectFolderDialog({ multiple: false }));
        if (typeof window.asperaOverrideShowSelectFolderDialog === 'function') {
            observable = window.asperaOverrideShowSelectFolderDialog({ multiple: false });
        }
        return observable.pipe(mergeMap(data => {
            if (data && data.dataTransfer && data.dataTransfer.files[0]) {
                const saveLocation = data.dataTransfer.files[0].name;
                connectOptions.use_absolute_destination_path = true;
                transferSpec.destination_root = saveLocation;
            }
            else {
                return throwError(() => {
                    return {
                        data: 'startTransferWithPrompt: Returned no file choices for download to prompt for Aspera Desktop',
                        aborted: true,
                    };
                });
            }
            return startDesktopTransfer(transferSpec, connectOptions, useDecrypt);
        }));
    }
    else if (((_b = window.asperaTransfer) === null || _b === void 0 ? void 0 : _b.connectRunning) && ((_c = window.asperaTransfer) === null || _c === void 0 ? void 0 : _c.asperaWeb)) {
        let observable = from(window.asperaTransfer.asperaWeb.showSelectFolderDialogPromise({ allowMultipleSelection: false }));
        if (typeof window.asperaOverrideShowSelectFolderDialog === 'function') {
            observable = window.asperaOverrideShowSelectFolderDialog({ multiple: false });
        }
        return observable.pipe(mergeMap(data => {
            var _a, _b;
            if (data && data.dataTransfer && data.dataTransfer.files[0]) {
                const saveLocation = data.dataTransfer.files[0].name;
                connectOptions.use_absolute_destination_path = true;
                transferSpec.destination_root = saveLocation;
            }
            else {
                return throwError(() => {
                    return {
                        data: 'startTransferWithPrompt: Returned no file choices for download to prompt',
                        aborted: true,
                    };
                });
            }
            if (((_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.connectRunning) && ((_b = window.asperaTransfer) === null || _b === void 0 ? void 0 : _b.asperaWeb)) {
                return startConnectTransfer(transferSpec, connectOptions, useDecrypt);
            }
            else {
                return throwError(() => new AsperaError('startTransferWithPrompt: Encountered an issue with save to prompt').getError());
            }
        }));
    }
    else {
        return throwError(() => new AsperaError('startTransferWithPrompt: Encountered an issue with save to prompt').getError());
    }
};
/**
 * Start a transfer
 *
 * @param transferSpec - a transferSpec describing the transfer
 * @param id - ID of transfer group (logical area of the app)
 * @param disableConnectPaths - indicate if connect paths should be disabled
 * @param promptForSaveTo - Indicate if connect should prompt to where to save to (only for Connect Downloads)
 * @param useDecrypt - Indicate that decryption should be prompted (EAR passphrase prompt)
 * @param previewData - Base64 image for downloading single files with previews (Desktop only)
 *
 * @returns an observable that has the request_id if able to create transfer
 */
export const startTransfer = (transferSpec, id, disableConnectPaths, promptForSaveTo, useDecrypt, previewData) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const connectOptions = {
        allow_dialogs: false,
    };
    if (disableConnectPaths) {
        connectOptions.return_files = false;
        connectOptions.return_paths = false;
    }
    const fileExtension = ((_a = transferSpec.paths) === null || _a === void 0 ? void 0 : _a.map(item => {
        var _a;
        return (((_a = item.source) === null || _a === void 0 ? void 0 : _a.split('.').pop()) || '').toLowerCase();
    })) || [];
    if (fileExtension.length && fileExtension.filter(item => item === 'aspera-env').length === fileExtension.length) {
        useDecrypt = true;
    }
    if (((_c = (_b = transferSpec.tags) === null || _b === void 0 ? void 0 : _b.aspera) === null || _c === void 0 ? void 0 : _c.aspera_ui) && useDecrypt) {
        transferSpec.tags.aspera.aspera_ui.attemptDecryption = useDecrypt;
    }
    if (runningWithAsperaMobile() && !((_e = (_d = window.asperaTransfer) === null || _d === void 0 ? void 0 : _d.transferSystemData) === null || _e === void 0 ? void 0 : _e.disableConnect)) {
        sendMobileSdkMessage({
            purpose: 'START_TRANSFER',
            data: transferSpec,
        });
        if (!getStorageItem(storageKeys.mobileTransferNotification)) {
            notification.create('info', language.get('transferSystem.mobileTransferStarted'), language.get('transferSystem.mobileTransfersHelper'));
            setStorageItem(storageKeys.mobileTransferNotification, 'TRUE');
        }
        return of({ mobileTransfer: true, transferSpec });
    }
    if ((_f = window.asperaTransfer) === null || _f === void 0 ? void 0 : _f.desktopSdkRunning) {
        if (previewData) {
            connectOptions.preview_base64 = previewData;
        }
        if (promptForSaveTo && transferSpec.direction === 'receive') {
            return startTransferWithPrompt(transferSpec, connectOptions, useDecrypt);
        }
        else {
            return startDesktopTransfer(transferSpec, connectOptions, useDecrypt);
        }
    }
    else if (((_g = window.asperaTransfer) === null || _g === void 0 ? void 0 : _g.connectRunning) && ((_h = window.asperaTransfer) === null || _h === void 0 ? void 0 : _h.asperaWeb)) {
        if (promptForSaveTo && transferSpec.direction === 'receive') {
            return startTransferWithPrompt(transferSpec, connectOptions, useDecrypt);
        }
        else {
            return startConnectTransfer(transferSpec, connectOptions, useDecrypt);
        }
    }
    else if ((_j = window.asperaTransfer) === null || _j === void 0 ? void 0 : _j.httpGatewayRunning) {
        // When in mobile downloads via IFRAME do not work; need to launch native browser. openNewTab is safe in this async as it will trigger mobile interceptor.
        if (runningWithAsperaMobile() && transferSpec.direction === 'receive') {
            return from(download(transferSpec, { disableAutoDownload: true })).pipe(map(response => {
                if (response && response.url) {
                    openNewTab(response.url);
                }
                return response;
            }));
        }
        return from(transferSpec.direction === 'receive' ? download(transferSpec) : upload(transferSpec, id));
    }
    else {
        return throwError(() => new AsperaError('startTransfer: No transfer method is available for starting a transfer').getError());
    }
};
/**
 * Get a list of all initial transfers in connect. This is used as a quick way to get the initial list before transfer callback
 *
 * @returns an observable that completes with all transfers.
 */
export const getAllCurrentTransfers = () => {
    const transfers = getProperListOfTransfers();
    return of({ transfers, iteration_token: '', result_count: transfers.length });
};
/**
 * Open the system folder to show the files downloaded
 *
 * @param id - the ID of the transfer to open
 *
 * @returns an observable indicating success
 */
export const showDirectory = (id) => {
    var _a, _b;
    if ((_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.desktopSdkRunning) {
        return from(desktopShowDirectory(id));
    }
    else if ((_b = window.asperaTransfer) === null || _b === void 0 ? void 0 : _b.asperaWeb) {
        return from(window.asperaTransfer.asperaWeb.showDirectory(id));
    }
    else {
        return throwError(() => new AsperaError('showDirectory: Client is not available to open the file system').getError());
    }
};
/**
 * Pause a transfer
 *
 * @param id - the ID of the transfer to pause
 *
 * @returns an observable indicating success
 */
export const pauseTransfer = (id) => {
    var _a, _b, _c, _d;
    if ((_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.desktopSdkRunning) {
        return from(desktopStopTransfer(id));
    }
    else if ((_b = window.asperaTransfer) === null || _b === void 0 ? void 0 : _b.asperaWeb) {
        const transfer = (_d = (_c = window.asperaTransfer) === null || _c === void 0 ? void 0 : _c.allTransfers) === null || _d === void 0 ? void 0 : _d.get(id);
        if (transfer)
            transfer.status = 'cancelled';
        return from(window.asperaTransfer.asperaWeb.stopTransfer(id));
    }
    else {
        return throwError(() => new AsperaError('pauseTransfer: Client is not available to pause a transfer').getError());
    }
};
/**
 * Modify a transfer
 *
 * @param id - the ID of the transfer to modify
 *
 * @returns an observable indicating success
 */
export const modifyTransfer = (id, data) => {
    var _a, _b;
    if ((_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.desktopSdkRunning) {
        return from(desktopModifyTransfer(id, data));
    }
    else if ((_b = window.asperaTransfer) === null || _b === void 0 ? void 0 : _b.asperaWeb) {
        return from(window.asperaTransfer.asperaWeb.modifyTransfer(id, data));
    }
    else {
        return throwError(() => new AsperaError('modifyTransfer: Client is not available to resume a transfer').getError());
    }
};
/**
 * Resume a transfer
 *
 * @param id - the ID of the transfer to resume
 *
 * @returns an observable indicating success
 */
export const resumeTransfer = (id) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    if (runningWithAsperaMobile() && !((_b = (_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.transferSystemData) === null || _b === void 0 ? void 0 : _b.disableConnect)) {
        sendMobileSdkMessage({
            purpose: 'RESUME_TRANSFER',
            data: id,
        });
        return of({ mobileTransfer: true, id });
    }
    if (((_c = window.asperaTransfer) === null || _c === void 0 ? void 0 : _c.desktopSdkRunning) || ((_d = window.asperaTransfer) === null || _d === void 0 ? void 0 : _d.asperaWeb)) {
        const transfer = (_f = (_e = window.asperaTransfer) === null || _e === void 0 ? void 0 : _e.allTransfers) === null || _f === void 0 ? void 0 : _f.get(id);
        if (transfer)
            transfer.status = 'queued';
    }
    if ((_g = window.asperaTransfer) === null || _g === void 0 ? void 0 : _g.desktopSdkRunning) {
        return from(desktopResumeTransfer(id, {}));
    }
    else if ((_h = window.asperaTransfer) === null || _h === void 0 ? void 0 : _h.asperaWeb) {
        return from(window.asperaTransfer.asperaWeb.resumeTransfer(id, {}));
    }
    else {
        return throwError(() => new AsperaError('resumeTransfer: Client is not available to resume a transfer').getError());
    }
};
/**
 * Cancel a transfer
 *
 * @param id - the ID of the transfer to cancel
 *
 * @returns an observable indicating success
 */
export const cancelTransferStarter = (id) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    if (runningWithAsperaMobile() && !((_b = (_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.transferSystemData) === null || _b === void 0 ? void 0 : _b.disableConnect)) {
        sendMobileSdkMessage({
            purpose: 'STOP_TRANSFER',
            data: id,
        });
        return of({ mobileTransfer: true, id });
    }
    if ((_c = window.asperaTransfer) === null || _c === void 0 ? void 0 : _c.desktopSdkRunning) {
        return from(desktopRemoveTransfer(id));
    }
    else if (((_d = window.asperaTransfer) === null || _d === void 0 ? void 0 : _d.connectRunning) && ((_e = window.asperaTransfer) === null || _e === void 0 ? void 0 : _e.asperaWeb)) {
        (_g = (_f = window.asperaTransfer) === null || _f === void 0 ? void 0 : _f.allTransfers) === null || _g === void 0 ? void 0 : _g.delete(id);
        return from(window.asperaTransfer.asperaWeb.removeTransfer(id));
    }
    else if ((_h = window.asperaTransfer) === null || _h === void 0 ? void 0 : _h.httpGatewayRunning) {
        cancelTransfer(id);
        return of(true);
    }
    else {
        return throwError(() => new AsperaError('cancelTransferStarter: Client is not available to cancel a transfer').getError());
    }
};
/**
 * Set a remote transfer (Node2Node) to monitor
 *
 * @param id - the ID of the transfer (uuid)
 * @param url - the URL that provides info about the transfer
 * @param ignoreStop - indicate if transfer should always poll (useful for Resume where transfers can take a while to update)
 */
export const setRemoteTransfer = (id, url, ignoreStop) => {
    var _a;
    if ((_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.transferPoller) {
        window.asperaTransfer.transferPoller.setTransfer(id, url, ignoreStop);
    }
};
/** Safely remove transfer from transfer poller */
export const removeTransferFromPoller = (id) => {
    var _a;
    if ((_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.transferPoller)
        window.asperaTransfer.transferPoller.removeTransfer(id);
};
/**
 * Remove a transfer from monitoring
 *
 * @param id - the ID of the transfer (uuid)
 * @param type - type transfer came from (if set will remove from that system as well as internal)
 */
export const removeTransferItem = (id, type) => {
    var _a, _b;
    if (type === 'http')
        removeTransfer(id);
    if (type === 'connect' && runningWithAsperaMobile()) {
        sendMobileSdkMessage({
            purpose: 'REMOVE_TRANSFER',
            data: id,
        });
    }
    if (type === 'desktop')
        desktopRemoveTransfer(id).then(() => { }).catch(() => { });
    if (type === 'connect' && ((_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.asperaWeb))
        window.asperaTransfer.asperaWeb.removeTransfer(id).then(() => { }).catch(() => { });
    if (type === 'node')
        removeTransferFromPoller(id);
    if ((_b = window.asperaTransfer) === null || _b === void 0 ? void 0 : _b.allTransfers) {
        window.asperaTransfer.allTransfers.delete(id);
    }
    pushTransfers([]);
};
/**
 * Filter inactive transfers from current list
 *
 * @param allTransfers - current transfer items
 * @returns the filtered transfer items
 */
export const getInactiveTransfers = (allTransfers) => {
    const activeTransferStatus = ['running', 'initiating', 'queued'];
    const inactiveTransfers = [];
    allTransfers.filter(transfer => {
        if (!activeTransferStatus.includes(transfer.status)) {
            inactiveTransfers.push(transfer);
        }
    });
    return inactiveTransfers;
};
/**
 * Clear all inactive transfers from the current list of transfers in memory
 */
export const clearInactiveTransfers = (callback) => {
    const allTransfers = getProperListOfTransfers();
    const inactiveTransfers = getInactiveTransfers(allTransfers);
    inactiveTransfers.forEach(transfer => {
        if (transfer.uuid) {
            let transferType = 'connect';
            if (transfer.httpGatewayTransfer) {
                transferType = 'http';
            }
            else if (transfer.desktopSdkTransfer) {
                transferType = 'desktop';
            }
            removeTransferItem(transfer.uuid, transferType);
        }
        else {
            removeTransferItem(transfer.id, 'node');
        }
        if (typeof callback === 'function') {
            callback(inactiveTransfers);
        }
    });
};
export const getTransferSystemStatus = () => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    let type = 'none';
    if ((_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.desktopSdkRunning) {
        type = 'desktop';
    }
    else if ((_b = window.asperaTransfer) === null || _b === void 0 ? void 0 : _b.connectRunning) {
        type = 'connect';
    }
    else if (runningWithAsperaMobile()) {
        type = 'mobile';
    }
    else if ((_c = window.asperaTransfer) === null || _c === void 0 ? void 0 : _c.httpGatewayRunning) {
        type = 'http';
    }
    return {
        type,
        connectStatus: (_d = window.asperaTransfer) === null || _d === void 0 ? void 0 : _d.connectStatus,
        connectAvailable: !!((_e = window.asperaTransfer) === null || _e === void 0 ? void 0 : _e.connectRunning) || !!((_f = window.asperaTransfer) === null || _f === void 0 ? void 0 : _f.desktopSdkRunning),
        connectAppVersion: (_g = window.asperaTransfer) === null || _g === void 0 ? void 0 : _g.connectAppVersion,
        httpVersion: (_h = window.asperaTransfer) === null || _h === void 0 ? void 0 : _h.httpVersion,
        httpServerUrl: (_j = window.asperaTransfer) === null || _j === void 0 ? void 0 : _j.httpGatewayUrl,
        supportsDownloadTo: type === 'connect' || type === 'desktop',
        preferDesktop: !!((_k = window.asperaTransfer) === null || _k === void 0 ? void 0 : _k.preferringDestkop),
    };
};
/** Get the transfer system data set on initTransfer */
export const getTransferSystemData = () => {
    var _a;
    return ((_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.transferSystemData) || {};
};
/**
 * Show the connect installer flow after reloading the page.
 */
export const showConnectInstallFlow = () => {
    removeStorageItem(storageKeys.skipTransferSystem);
    removeStorageItem(storageKeys.transferSystemChoice);
    setStorageItem(storageKeys.skipTransferSystemWelcome, 'TRUE');
    if (getTransferSystemStatus().preferDesktop) {
        tracking.openDesktopInstaller();
    }
    else {
        window.location.reload();
    }
};
/**
 * Opt into desktop mode for the session.
 */
export const optIntoDesktopMode = () => {
    if (window.asperaTransfer) {
        window.asperaTransfer.preferringDestkop = true;
    }
};
/**
 * Switch to IBM Aspera Desktop SDK. This will make the Desktop SDK be used instead of Connect.
 * Call this after successfully starting Desktop
 *
 * @returns an observable that indicates success on starting and getting initial transfers
 */
export const switchToDesktopSDK = () => {
    var _a;
    if (window.asperaTransfer) {
        window.asperaTransfer.desktopSdkRunning = true;
        window.asperaTransfer.connectStatus = 'DESKTOP_SKIP';
        window.asperaTransfer.connectRunning = false;
        (_a = window.asperaTransfer.connectStatusChange) === null || _a === void 0 ? void 0 : _a.next('DESKTOP_START');
        setupTransferMonitor();
        setupDropWatch();
    }
    return from(desktopGetAllTransfers()).pipe(map(items => {
        parseDesktopTransfers(items);
        return true;
    }), catchError(error => {
        new AsperaError('switchToDesktopSDK: unable to get all desktop transfers', error);
        return of(true);
    }));
};
export const changeTransferClient = () => {
    tracking.setUserPreferences({ optIntoDesktop: null }, true, true);
    removeStorageItem(storageKeys.transferSystemChoice);
    tracking.track('Updated Object', { object: 'User', process: 'Switch Transfer Client', successFlag: true });
};
const exports = {
    initTransferSystem,
    monitorTransfers,
    openFileFolderPicker,
    startTransfer,
    getAllCurrentTransfers,
    showDirectory,
    pauseTransfer,
    resumeTransfer,
    cancelTransferStarter,
    setRemoteTransfer,
    getInactiveTransfers,
    removeTransferItem,
    monitorDrops,
    getTransferSystemStatus,
    openConnectPreferences,
    clearInactiveTransfers,
    startHttpGateway,
    setConnectStatus,
    pushTransfers,
    showConnectInstallFlow,
    watchConnectStatusChange,
    watchTransferStatusChange,
    getTransferSystemData,
    forceUpdateTransferMonitor,
    removeTransferFromPoller,
    getProperListOfTransfers,
    getRunningHttpGatewayTransfers,
    switchToDesktopSDK,
    changeTransferClient,
};
export default exports;
